<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

interface Props {
  disabled: boolean
  contextLabel: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'submit', text: string): void
}>()

const draft = ref('')

const handleSubmit = () => {
  const text = draft.value.trim()
  if (!text) return
  emit('submit', text)
  draft.value = ''
}

// Enter sends; Shift+Enter inserts a newline. Matches mainstream chat UX.
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSubmit()
  }
}
</script>

<template>
  <Box direction="col" gap="2" class="composer">
    <Box align="center" gap="2" class="composer__context">
      <PiIcon name="bolt" class="composer__context-icon" />
      <Text type="caption" color="muted">{{ contextLabel }}</Text>
    </Box>

    <Box align="stretch" gap="2" class="composer__row">
      <Box class="composer__input" grow>
        <Textarea
          v-model="draft"
          placeholder="Ask about your portfolio, an offering, fees…"
          :rows="1"
          :auto-resize="true"
          :disabled="disabled"
          @keydown="handleKeydown"
        />
      </Box>
      <Box class="composer__send">
        <Button
          icon="arrow-up"
          size="medium"
          :disabled="disabled || draft.trim().length === 0"
          aria-label="Send message"
          @click="handleSubmit"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.composer {
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
}

.composer__context {
  padding: 0.25rem 0.5rem;
  background: var(--p-surface-100);
  border-radius: 999px;
  align-self: flex-start;
}

.composer__context-icon {
  font-size: 0.7rem;
  color: var(--p-primary-600);
}

/* Two distinct siblings — input on the left (full kit-styled textarea),
   send button on the right (matching height). `align: stretch` makes the
   send button fill the input's natural height so the edges line up. */
.composer__row {
  width: 100%;
}

.composer__input {
  min-width: 0;
  display: flex;
}

.composer__send {
  flex: 0 0 auto;
  display: flex;
}

.composer__send :deep(.base-button) {
  height: 100%;
  min-height: 44px;
  aspect-ratio: 1;
  padding: 0;
}
</style>
